import React, {useState} from 'react';
import { Box, Grid2 as Grid, TableContainer, Table, TableHead, TableRow, TableCell, useMediaQuery, useTheme, Paper, Typography, TableSortLabel, TableBody, Stack, styled, Link} from '@mui/material';
import { ExpenditureItem } from '../../../types/reports/expenditureItem';
import IndependentExpenditureItem from '../../../types/reports/independentExpenditureItem';
import { visuallyHidden } from '@mui/utils';

interface Props {
   tableRows?:ExpenditureItem[];
   ieTableRows?: IndependentExpenditureItem[];
}


const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: 'alternate.light',
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

const IndependentExpendituresScheduleTable = (props: Props): JSX.Element => {

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const [expenditureRows, setExpenditureRows] = useState<IndependentExpenditureItem[]>(props.ieTableRows ?? []);
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState<'desc' | 'asc'>('asc');

    let totalCount = props.tableRows?.length ?? 0;
    if(props.ieTableRows){
        totalCount = props.ieTableRows.length;
    }


    const sortByAmount = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return b.amount - a.amount;
            } else {
                return a.amount - b.amount;
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(orderBy === 'asc' ? 'desc' : 'asc');
    }

    const sortByDate = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            } else {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const sortByName = () => {
        const sortedRows = [...expenditureRows].sort((a, b) => {
            if (order === 'asc') {
                return b.fullNameReverse.localeCompare(a.fullNameReverse);
            } else {
                return a.fullNameReverse.localeCompare(b.fullNameReverse);
            }
        });
        setExpenditureRows(sortedRows);
        setOrder(order === 'asc' ? 'desc' : 'asc');
    }

    const updateSort = (column: string) => {
        setOrderBy(column);
    }

    return (
        <Grid container spacing={1} marginTop={1} marginBottom={1}>
         <TableContainer component={Paper}>
             <Table aria-label="independent expenditures table" size="small">
                 <TableHead>
                     <TableRow>
                         <TableCell onClick={sortByDate} onMouseEnter={()=>updateSort('date')}>
                            <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable' >
                                Transaction Date
                            </Typography>
                         </TableCell>
                         <TableCell>
                            <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable' >
                                Utilization Date
                            </Typography>
                         </TableCell>
                         <TableCell onClick={sortByName} onMouseEnter={()=>updateSort('name')}>
                            <TableSortLabel
                                active={orderBy === 'name'}
                                direction={orderBy === 'name' ? order : 'asc'}
                                onClick={sortByName}
                            >
                                <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable'>
                                    Name/Address
                                </Typography>
                                {orderBy === 'name' ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                         </TableCell>
                         {
                            isSM &&
                            <TableCell align='right' onClick={sortByAmount}>
                                <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable' >
                                    Amount
                                </Typography>
                            </TableCell>
                         }
                         <TableCell>
                             <Typography variant={'subtitle2'} fontWeight={600}>
                                Purpose
                             </Typography>
                         </TableCell>
                         <TableCell>
                             <Typography variant={'subtitle2'} fontWeight={600}>
                                Candidate/Position
                             </Typography>
                         </TableCell>
                         {
                            !isSM &&
                            <TableCell onClick={sortByAmount} align='right'>
                                <Typography variant={'subtitle2'} fontWeight={600} className='is-clickable' >
                                     Amount
                                </Typography>
                            </TableCell>
                         }
                     </TableRow>
                 </TableHead>
                 <TableBody>
                     {expenditureRows?.map((expenditure) => (
                         <StyledTableRow key={expenditure.id}>
                             <TableCell>
                                 <Typography variant={'body2'}>
                                     {expenditure.date}
                                 </Typography>
                             </TableCell>
                             <TableCell>
                                 <Typography variant={'body2'}>
                                     {expenditure.utilizationDate}
                                 </Typography>
                             </TableCell>
                             <TableCell>
                                <>
                                    <Typography variant="body2">
                                        {expenditure.fullNameReverse} {expenditure.vendor}
                                    </Typography>
                                    <Typography component="i"  variant="body2">{expenditure.fullAddress}</Typography>
                                </>       
                            </TableCell>
                             {
                                isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {expenditure.amount}
                                    </Typography>
                                </TableCell>
                             }
                             <TableCell>
                                <Typography variant="body2">{expenditure.description} {expenditure.purpose}</Typography>
                             </TableCell>
                             <TableCell>
                                <Stack>
                                    {
                                        expenditure.expenditurePosition?.length > 0 ?
                                        <Typography variant="body2" fontWeight={550}>in {expenditure.expenditurePosition} {expenditure.relatedCandidateName} ({expenditure.relatedCandidateCpfId})</Typography>
                                        :
                                        <Typography variant="body2" fontWeight={550}>{expenditure.relatedCandidateName} ({expenditure.relatedCandidateCpfId})</Typography>
                                    }
                                </Stack>   
                             </TableCell>
                             {
                                !isSM &&
                                <TableCell align='right'>
                                    <Typography variant={'body2'}>
                                        {expenditure.amount}
                                    </Typography>
                                </TableCell>
                             }   
                         </StyledTableRow>
                    ))}
                 </TableBody>
             </Table>
         </TableContainer>
        </Grid>
    );
}

export default IndependentExpendituresScheduleTable;